// Chemin: C:\gestion-copro\src\services\mandat.service.ts
// Service pour le mandat de coordination - Gouvernance V2
// Session 88 : création initiale
// Session 90 : Ajout envoyerNotificationMandat() pour notifier les membres CS par email
// Référence : SPECIFICATION_GOUVERNANCE_V2.md §5

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import { functions } from './firebase';
import {
  MandatCoordination,
  MandatCreateData,
  MandatSuivi,
  MandatSignatureDetail,
  MandatSignaturePageData,
  SignatureMandat,
  StatutMandat,
  genererTexteMandat,
  genererTexteConfirmation,
  getNbSignatures,
  toutesSignaturesCollectees
} from '../types/mandat.types';
import { getMembresCSByCopropriete } from './conseilSyndical.service';

// ============================================================================
// CHEMINS FIRESTORE
// ============================================================================

/**
 * Sous-collection : coproprietes/{coproprieteId}/mandats/{mandatId}
 * Isolation multi-tenant native
 */
function getMandatsCollectionPath(coproprieteId: string): string {
  return `coproprietes/${coproprieteId}/mandats`;
}

function getMandatDocPath(coproprieteId: string, mandatId: string): string {
  return `coproprietes/${coproprieteId}/mandats/${mandatId}`;
}

// ============================================================================
// CRÉATION DU MANDAT
// ============================================================================

/**
 * Crée un nouveau mandat de coordination
 * Appelé par l'Admin SaaS lors de l'onboarding de la copropriété
 * 
 * @returns ID du mandat créé
 */
export const createMandat = async (data: MandatCreateData): Promise<string> => {
  const now = Timestamp.now();
  
  // Générer un ID unique pour le mandat
  const mandatId = `mandat_${data.coproprieteId}_${Date.now()}`;
  
  // Construire l'objet signatures vide (une entrée attendue par membre CS)
  const signatures: MandatCoordination['signatures'] = {};
  
  const mandatData: Omit<MandatCoordination, 'id'> = {
    coproprieteId: data.coproprieteId,
    coordinateurId: data.coordinateurId,
    coordinateurNom: data.coordinateurNom,
    estMembreCS: data.estMembreCS,
    dateCreation: now,
    statut: 'en_attente_signatures',
    signatures,
    signaturesAttendues: data.membresCSIds.length,
    createdBy: data.coordinateurId, // Sera remplacé par l'uid de l'Admin SaaS si nécessaire
    nomCopropriete: data.nomCopropriete,
  };
  
  // Champs optionnels
  if (data.dateAG) {
    mandatData.dateAG = Timestamp.fromDate(data.dateAG);
  }
  if (data.refAG) {
    mandatData.refAG = data.refAG;
  }
  
  // Écrire dans Firestore
  const mandatRef = doc(db, getMandatDocPath(data.coproprieteId, mandatId));
  await setDoc(mandatRef, mandatData);
  
  // Mettre à jour la copropriété avec le mandatActifId et le statut
  const coproprieteRef = doc(db, 'coproprietes', data.coproprieteId);
  await updateDoc(coproprieteRef, {
    mandatActifId: mandatId,
    coordinateurId: data.coordinateurId,
    coordinateurNom: data.coordinateurNom,
    coordinateurEstMembreCS: data.estMembreCS,
    dateDesignationCoordinateur: now,
    statutOnboarding: 'en_attente_signatures',
    updatedAt: now
  });
  
  return mandatId;
};

// ============================================================================
// NOTIFICATION EMAIL MANDAT (Session 90)
// ============================================================================

/**
 * Résultat de l'envoi des notifications
 */
export interface NotificationMandatResult {
  total: number;
  envoyes: number;
  erreurs: string[];
}

/**
 * Envoie les notifications par email aux membres CS pour signer le mandat
 * Appelle la Cloud Function sendMandatNotification
 * 
 * À appeler APRÈS createMandat() depuis la page ConseilSyndicalPage
 */
export const envoyerNotificationMandat = async (
  coproprieteId: string,
  mandatId: string
): Promise<NotificationMandatResult> => {
  const sendMandatNotification = httpsCallable<
    { coproprieteId: string; mandatId: string },
    NotificationMandatResult
  >(functions, 'sendMandatNotification');

  const result = await sendMandatNotification({ coproprieteId, mandatId });
  return result.data;
};

// ============================================================================
// LECTURE DU MANDAT
// ============================================================================

/**
 * Récupère un mandat par son ID
 */
export const getMandatById = async (
  coproprieteId: string,
  mandatId: string
): Promise<MandatCoordination | null> => {
  const mandatRef = doc(db, getMandatDocPath(coproprieteId, mandatId));
  const mandatSnap = await getDoc(mandatRef);
  
  if (!mandatSnap.exists()) {
    return null;
  }
  
  return {
    id: mandatSnap.id,
    ...mandatSnap.data()
  } as MandatCoordination;
};

/**
 * Récupère le mandat actif d'une copropriété
 * Cherche d'abord par mandatActifId sur la copropriété,
 * sinon cherche le dernier mandat actif dans la sous-collection
 */
export const getMandatActif = async (
  coproprieteId: string
): Promise<MandatCoordination | null> => {
  // 1. Essayer via le champ mandatActifId de la copropriété
  const coproprieteRef = doc(db, 'coproprietes', coproprieteId);
  const coproprieteSnap = await getDoc(coproprieteRef);
  
  if (coproprieteSnap.exists()) {
    const mandatActifId = coproprieteSnap.data()?.mandatActifId;
    if (mandatActifId) {
      const mandat = await getMandatById(coproprieteId, mandatActifId);
      if (mandat && (mandat.statut === 'actif' || mandat.statut === 'en_attente_signatures')) {
        return mandat;
      }
    }
  }
  
  // 2. Fallback : chercher dans la sous-collection
  const q = query(
    collection(db, getMandatsCollectionPath(coproprieteId)),
    where('statut', 'in', ['actif', 'en_attente_signatures']),
    orderBy('dateCreation', 'desc'),
    limit(1)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }
  
  const docSnap = snapshot.docs[0];
  return {
    id: docSnap.id,
    ...docSnap.data()
  } as MandatCoordination;
};

/**
 * Récupère tous les mandats d'une copropriété (historique)
 */
export const getMandatsByCopropriete = async (
  coproprieteId: string
): Promise<MandatCoordination[]> => {
  const q = query(
    collection(db, getMandatsCollectionPath(coproprieteId)),
    orderBy('dateCreation', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data()
  } as MandatCoordination));
};

// ============================================================================
// SIGNATURE DU MANDAT
// ============================================================================

/**
 * Enregistre la signature d'un membre CS
 * Appelé quand un membre CS confirme le mandat à sa première connexion
 * 
 * Si toutes les signatures sont collectées, active automatiquement le mandat
 * et passe la copropriété en statut 'operationnelle'
 * 
 * @returns true si le mandat vient d'être activé (toutes signatures collectées)
 */
export const signerMandat = async (
  coproprieteId: string,
  mandatId: string,
  membreCSId: string,
  userId: string,
  nom: string,
  ip: string
): Promise<boolean> => {
  const now = Timestamp.now();
  
  // 1. Récupérer le mandat
  const mandat = await getMandatById(coproprieteId, mandatId);
  if (!mandat) {
    throw new Error('Mandat introuvable');
  }
  
  if (mandat.statut !== 'en_attente_signatures') {
    throw new Error(`Impossible de signer : le mandat est en statut "${mandat.statut}"`);
  }
  
  // 2. Vérifier que ce membre n'a pas déjà signé
  if (mandat.signatures[membreCSId]) {
    throw new Error('Ce membre a déjà signé le mandat');
  }
  
  // 3. Construire la signature
  const texteConfirmation = genererTexteConfirmation(
    mandat.coordinateurNom,
    mandat.nomCopropriete
  );
  
  const signature: SignatureMandat = {
    membreCSId,
    userId,
    nom,
    dateSignature: now,
    ip,
    confirmationTexte: texteConfirmation
  };
  
  // 4. Enregistrer la signature dans le mandat
  const mandatRef = doc(db, getMandatDocPath(coproprieteId, mandatId));
  await updateDoc(mandatRef, {
    [`signatures.${membreCSId}`]: signature
  });
  
  // 5. Vérifier si toutes les signatures sont collectées
  const nbSignaturesApres = getNbSignatures(mandat) + 1; // +1 car la MAJ Firestore est async
  const toutSigne = nbSignaturesApres >= mandat.signaturesAttendues;
  
  if (toutSigne) {
    await activerMandat(coproprieteId, mandatId);
  }
  
  return toutSigne;
};

// ============================================================================
// ACTIVATION DU MANDAT
// ============================================================================

/**
 * Active le mandat (appelé automatiquement quand toutes les signatures sont collectées)
 * - Passe le mandat en statut 'actif'
 * - Passe la copropriété en statut 'operationnelle'
 */
export const activerMandat = async (
  coproprieteId: string,
  mandatId: string
): Promise<void> => {
  const now = Timestamp.now();
  
  // 1. Mettre à jour le mandat
  const mandatRef = doc(db, getMandatDocPath(coproprieteId, mandatId));
  await updateDoc(mandatRef, {
    statut: 'actif' as StatutMandat,
    dateActivation: now
  });
  
  // 2. Mettre à jour la copropriété
  const coproprieteRef = doc(db, 'coproprietes', coproprieteId);
  await updateDoc(coproprieteRef, {
    statutOnboarding: 'operationnelle',
    updatedAt: now
  });
};

// ============================================================================
// RÉVOCATION DU MANDAT
// ============================================================================

/**
 * Révoque un mandat actif
 * Utilisé en cas de changement de coordinateur ou vote de révocation
 */
export const revoquerMandat = async (
  coproprieteId: string,
  mandatId: string
): Promise<void> => {
  const now = Timestamp.now();
  
  // 1. Mettre à jour le mandat
  const mandatRef = doc(db, getMandatDocPath(coproprieteId, mandatId));
  await updateDoc(mandatRef, {
    statut: 'revoque' as StatutMandat,
    dateRevocation: now
  });
  
  // 2. Retirer le mandatActifId de la copropriété
  const coproprieteRef = doc(db, 'coproprietes', coproprieteId);
  await updateDoc(coproprieteRef, {
    mandatActifId: null,
    statutOnboarding: 'suspendue',
    updatedAt: now
  });
};

// ============================================================================
// DONNÉES POUR LE DASHBOARD (SUIVI SIGNATURES)
// ============================================================================

/**
 * Récupère les données de suivi du mandat pour le dashboard coordinateur
 * Croise les membres CS actifs avec les signatures enregistrées
 */
export const getMandatSuivi = async (
  coproprieteId: string,
  mandatId: string
): Promise<MandatSuivi | null> => {
  // 1. Récupérer le mandat
  const mandat = await getMandatById(coproprieteId, mandatId);
  if (!mandat) return null;
  
  // 2. Récupérer les membres CS actifs
  const membresCS = await getMembresCSByCopropriete(coproprieteId, true);
  
  // 3. Construire le détail des signatures
  const detailSignatures: MandatSignatureDetail[] = membresCS.map(membre => {
    const signature = mandat.signatures[membre.id];
    return {
      membreCSId: membre.id,
      nom: `${membre.prenom} ${membre.nom}`,
      signe: !!signature,
      dateSignature: signature?.dateSignature
    };
  });
  
  return {
    id: mandat.id,
    statut: mandat.statut,
    coordinateurNom: mandat.coordinateurNom,
    estMembreCS: mandat.estMembreCS,
    signaturesAttendues: mandat.signaturesAttendues,
    signaturesRecues: getNbSignatures(mandat),
    detailSignatures,
    dateCreation: mandat.dateCreation,
    dateActivation: mandat.dateActivation
  };
};

// ============================================================================
// DONNÉES POUR LA PAGE DE SIGNATURE (MEMBRE CS)
// ============================================================================

/**
 * Récupère les données nécessaires pour la page de signature d'un membre CS
 * 
 * @param membreCSId - ID du document dans la collection membresCS
 * @returns null si pas de mandat en attente ou si le membre a déjà signé
 */
export const getMandatSignaturePageData = async (
  coproprieteId: string,
  membreCSId: string
): Promise<MandatSignaturePageData | null> => {
  // 1. Récupérer le mandat actif/en attente
  const mandat = await getMandatActif(coproprieteId);
  if (!mandat) return null;
  
  // Si le mandat est déjà actif, pas besoin de signer
  if (mandat.statut === 'actif') return null;
  
  // 2. Vérifier si ce membre a déjà signé
  const dejaSigne = !!mandat.signatures[membreCSId];
  
  // 3. Générer le texte du mandat
  const dateAGStr = mandat.dateAG 
    ? mandat.dateAG.toDate().toLocaleDateString('fr-FR', { 
        day: 'numeric', month: 'long', year: 'numeric' 
      }) 
    : undefined;
  
  const texteMandat = genererTexteMandat(
    mandat.nomCopropriete,
    mandat.coordinateurNom,
    dateAGStr
  );
  
  return {
    mandatId: mandat.id,
    coproprieteId: mandat.coproprieteId,
    nomCopropriete: mandat.nomCopropriete,
    coordinateurNom: mandat.coordinateurNom,
    estMembreCS: mandat.estMembreCS,
    dateAG: mandat.dateAG,
    dejaSigne,
    texteMandat
  };
};

// ============================================================================
// VÉRIFICATIONS
// ============================================================================

/**
 * Vérifie si un membre CS doit signer un mandat à sa connexion
 * Utilisé par ProtectedRoute pour rediriger vers la page de signature
 * 
 * @returns mandatId si signature requise, null sinon
 */
export const checkSignatureRequise = async (
  coproprieteId: string,
  userId: string
): Promise<string | null> => {
  // 1. Récupérer le mandat en attente
  const mandat = await getMandatActif(coproprieteId);
  if (!mandat || mandat.statut !== 'en_attente_signatures') {
    return null;
  }
  
  // 2. Le coordinateur ne signe pas son propre mandat
  if (mandat.coordinateurId === userId) {
    return null;
  }
  
  // 3. Chercher le membreCSId correspondant à ce userId
  const membresCS = await getMembresCSByCopropriete(coproprieteId, true);
  const membreCS = membresCS.find(m => m.userId === userId);
  
  if (!membreCS) {
    // Pas un membre CS → pas de signature requise
    return null;
  }
  
  // 4. Vérifier si déjà signé
  if (mandat.signatures[membreCS.id]) {
    return null; // Déjà signé
  }
  
  // Signature requise
  return mandat.id;
};

/**
 * Vérifie si la copropriété a un mandat actif
 */
export const hasMandat = async (coproprieteId: string): Promise<boolean> => {
  const mandat = await getMandatActif(coproprieteId);
  return mandat !== null && mandat.statut === 'actif';
};

/**
 * Récupère le membreCSId d'un utilisateur pour une copropriété
 * Utile pour la signature (on a besoin du membreCSId, pas du userId)
 */
export const getMembreCSIdByUserId = async (
  coproprieteId: string,
  userId: string
): Promise<string | null> => {
  const membresCS = await getMembresCSByCopropriete(coproprieteId, true);
  const membre = membresCS.find(m => m.userId === userId);
  return membre?.id || null;
};
